import { useCallback, useEffect, useMemo, useState } from "react";

import * as m from "../../../core/model/model";
import { useLazyApi } from "../../../hooks/useApi/useApi";
import * as proxy from "../../../services/api/proxy";

/**
 * Represents the current state of a Svea BankID authentication flow.
 *
 * The `isTerminal` property indicates whether the process has reached its final state.
 * When `isTerminal` is `true`, no further state changes will occur and the caller can
 * safely act on the outcome.
 *
 * Non-terminal states:
 * - `Initializing`         â€“ The start request is in flight.
 * - `PendingStart`         â€“ BankID session started; waiting for the user to open the BankID app.
 *                            A `qrCode` may be present for QR-based flows.
 * - `AwaitingAuthorization`â€“ The user has opened BankID and must approve the request.
 * - `Cancelling`           â€“ A cancellation has been requested; still waiting for confirmation.
 * - `Completing`           â€“ BankID approved; completing the session on the backend.
 *
 * Terminal states:
 * - `Completed`            â€“ Authentication succeeded. `bearerToken` and `refreshToken` are available.
 * - `Cancelled`            â€“ The process was cancelled (either by the caller or by the user in BankID).
 * - `Failed`               â€“ BankID reported a failure. `reason` describes the cause.
 * - `Error`                â€“ An unexpected error occurred. `message` may contain details.
 */
export type AuthenticationState =
	| { type: "Initializing"; isTerminal: false }
	| { type: "PendingStart"; isTerminal: false; qrCode?: string }
	| { type: "AwaitingAuthorization"; isTerminal: false }
	| { type: "Cancelling"; isTerminal: false }
	| { type: "Completing"; isTerminal: false }
	| {
			type: "Completed";
			isTerminal: true;
			bearerToken: string;
			refreshToken: string;
			delegateInformation: DelegateInformation;
	  }
	| { type: "Cancelled"; isTerminal: true }
	| { type: "Failed"; isTerminal: true; reason: string; message?: string }
	| { type: "Error"; isTerminal: true; message?: string };

export type DelegateInformation = {
	correspondenceName: string;
	firstName: string;
	lastName: string;
	personalNumber: string;
	psus: {
		psuId: string;
		sveaDelegateId: string;
		accessLevel:
			| {
					id: "restricted";
					contexts: {
						bankAccountId: string;
						accessLevel: "read" | "preparePayment" | "approvePayment";
					}[];
			  }
			| { id: "fullAccess" }
			| { id: "approve" }
			| { id: "prepare" }
			| { id: "cardHolder" };
	}[];
	forPsu: (psuId: string) => DelegateInformation["psus"][number] | null;
};

export interface NewSveaBankIdAuthFlowResult {
	/** The current state of the authentication flow. Poll this to react to progress. */
	state: AuthenticationState;

	/**
	 * Resets the current authentication flow and starts a new one.
	 * If a session is already in progress, a cancel request is sent to the backend
	 * (fire-and-forget) before the state is reset and the flow restarts.
	 * Safe to call at any point, including from terminal states.
	 */
	retry: () => void;

	/**
	 * Requests cancellation of the ongoing authentication process.
	 * The flow may not stop immediately â€” the state will transition through `Cancelling`
	 * and eventually settle on `Cancelled`. Safe to call at any point before a terminal state.
	 */
	cancel: () => void;
	/** Internal diagnostic data intended for debugging. Do not rely on this in production logic. */
	debugInfo: Record<string, unknown>;
	/**
	 * The BankID autostart token returned by the backend after starting the flow.
	 * Available once the flow has progressed past `Initializing`. Use this to deep-link
	 * into the native BankID app (e.g. via an `bankid://` URI on mobile).
	 */
	bankIdToken?: string;
}

/**
 * Drives a complete Svea BankID authentication flow.
 *
 * **Lifecycle:** The hook starts the authentication process automatically on mount â€” no
 * explicit trigger is required. From that point it polls the backend and progresses
 * through a sequence of states, reflected in the returned `authState`. The caller should
 * re-render on state changes and act once a terminal state is reached
 * (`authState.isTerminal === true`).
 *
 * **Cancellation:** Call `cancel()` at any time to abort the process. The hook will send
 * a cancel request to the backend and continue polling until BankID confirms the
 * cancellation, at which point `authState` will be `{ type: "Cancelled", isTerminal: true }`.
 *
 * **Terminal states:** Once `isTerminal` is `true` the hook will no longer update.
 * - `Completed` â€“ Authentication succeeded; use `bearerToken` / `refreshToken` to proceed.
 * - `Cancelled` â€“ The flow was aborted.
 * - `Failed`    â€“ BankID reported a failure; inspect `reason` for the cause.
 * - `Error`     â€“ An unexpected error occurred.
 *
 * @example
 * const { authState, cancel } = useNewSveaBankIdAuthFlow();
 * if (authState.isTerminal) { ... }
 * if (authState.type === "Completed") { doSomethingWith(authState.bearerToken); }
 */
export const useNewSveaBankIdAuthFlow = (): NewSveaBankIdAuthFlowResult => {
	const [start, startRequest, resetStart] = useLazyApi(proxy.Bank.SveaAuth2Controller.Start.Post);
	const [getStatus, getStatusRequest, resetGetStatus] = useLazyApi(proxy.Bank.SveaAuth2Controller.Status.Get);
	const [complete, completeRequest, resetComplete] = useLazyApi(proxy.Bank.SveaAuth2Controller.Complete.Post);
	const [cancel, cancelRequest, resetCancel] = useLazyApi(proxy.Bank.SveaAuth2Controller.Cancel.Post);

	const [isCancelling, setIsCancelling] = useState(false);

	//These are just helpers to make the state calculations easier to read and avoid repeating the same conditions multiple times. They don't represent any specific state on their own.
	const stateHelper_bankIdToken = useMemo(() => {
		return startRequest.data?.Data?.BankIdToken;
	}, [startRequest.data?.Data?.BankIdToken]);

	const stateHelper_startIsCompleted = useMemo(() => {
		return !startRequest.isLoading && !!startRequest.data;
	}, [startRequest.isLoading, startRequest.data]);

	const stateHelper_completeIsCompleted = useMemo(() => {
		return !completeRequest.isLoading && !!completeRequest.data?.Data;
	}, [completeRequest.isLoading, completeRequest.data?.Data]);

	const stateHelper_completeHasError = useMemo(
		() => !completeRequest.isLoading && !!completeRequest.data && !completeRequest.data.Success,
		[completeRequest.isLoading, completeRequest.data],
	);

	//These are the possible internal states of the authentication flow, based on the requests and helper states. They are used to determine what to show to the user and when to call getStatus or complete.
	//One and only one of these state should be true at any given time. Any overlap or scenario where none of them are true should be investigated and likely indicates a missing state or a bug in the flow.
	const state_initializing = useMemo(
		() => !isCancelling && !startRequest.isLoading && !startRequest.data && !startRequest.error,
		[isCancelling, startRequest.isLoading, startRequest.data, startRequest.error],
	);
	const state_startFailed = useMemo(
		() => !isCancelling && !startRequest.isLoading && !!startRequest.error,
		[isCancelling, startRequest.isLoading, startRequest.error],
	);
	const state_startRequested = useMemo(
		() => !isCancelling && startRequest.isLoading,
		[isCancelling, startRequest.isLoading],
	);
	const state_started = useMemo(
		() => !isCancelling && stateHelper_startIsCompleted && !getStatusRequest.isLoading && !getStatusRequest.data,
		[isCancelling, getStatusRequest.data, getStatusRequest.isLoading, stateHelper_startIsCompleted],
	);
	const state_runloop_fetchingStatus = useMemo(
		() =>
			!isCancelling && stateHelper_startIsCompleted && !stateHelper_completeIsCompleted && getStatusRequest.isLoading,
		[isCancelling, getStatusRequest.isLoading, stateHelper_startIsCompleted, stateHelper_completeIsCompleted],
	);
	const state_runlookp_fetchingStatusFailed = useMemo(
		() =>
			!isCancelling &&
			stateHelper_startIsCompleted &&
			!stateHelper_completeIsCompleted &&
			!getStatusRequest.isLoading &&
			getStatusRequest.error,
		[
			isCancelling,
			getStatusRequest.error,
			getStatusRequest.isLoading,
			stateHelper_startIsCompleted,
			stateHelper_completeIsCompleted,
		],
	);

	const state_running_hasInteractionStatus = useMemo(
		() =>
			!isCancelling &&
			stateHelper_startIsCompleted &&
			!stateHelper_completeIsCompleted &&
			!getStatusRequest.isLoading &&
			(getStatusRequest.data?.Data?.Status ===
				m.Bokio.Bank.Web.Controllers.AuthStatusResultStatusEnum.AwaitingAuthorization ||
				getStatusRequest.data?.Data?.Status === m.Bokio.Bank.Web.Controllers.AuthStatusResultStatusEnum.PendingStart),
		[
			isCancelling,
			getStatusRequest.data?.Data?.Status,
			getStatusRequest.isLoading,
			stateHelper_startIsCompleted,
			stateHelper_completeIsCompleted,
		],
	);
	const state_hasNonInteractionStatus = useMemo(
		() =>
			!isCancelling &&
			stateHelper_startIsCompleted &&
			!stateHelper_completeIsCompleted &&
			!getStatusRequest.isLoading &&
			!!getStatusRequest.data &&
			(getStatusRequest.data?.Data?.Status ===
				m.Bokio.Bank.Web.Controllers.AuthStatusResultStatusEnum.Failed_BankIdTokenAlreadyUsed ||
				getStatusRequest.data?.Data?.Status ===
					m.Bokio.Bank.Web.Controllers.AuthStatusResultStatusEnum.Failed_ExpiredTransaction ||
				getStatusRequest.data?.Data?.Status ===
					m.Bokio.Bank.Web.Controllers.AuthStatusResultStatusEnum.Failed_StartFailed ||
				getStatusRequest.data?.Data?.Status ===
					m.Bokio.Bank.Web.Controllers.AuthStatusResultStatusEnum.Failed_UnknownReason ||
				getStatusRequest.data?.Data?.Status === m.Bokio.Bank.Web.Controllers.AuthStatusResultStatusEnum.Error),
		[
			isCancelling,
			stateHelper_startIsCompleted,
			stateHelper_completeIsCompleted,
			getStatusRequest.data,
			getStatusRequest.isLoading,
		],
	);
	const state_successful = useMemo(
		() =>
			!isCancelling &&
			stateHelper_startIsCompleted &&
			!stateHelper_completeIsCompleted &&
			!stateHelper_completeHasError &&
			!getStatusRequest.isLoading &&
			getStatusRequest.data?.Data?.Status === m.Bokio.Bank.Web.Controllers.AuthStatusResultStatusEnum.Successful,
		[
			isCancelling,
			getStatusRequest.data?.Data?.Status,
			getStatusRequest.isLoading,
			stateHelper_startIsCompleted,
			stateHelper_completeIsCompleted,
			stateHelper_completeHasError,
		],
	);
	const state_completed = useMemo(
		() => !isCancelling && stateHelper_completeIsCompleted,
		[isCancelling, stateHelper_completeIsCompleted],
	);
	const state_completedWithError = useMemo(
		() => !isCancelling && stateHelper_completeHasError,
		[isCancelling, stateHelper_completeHasError],
	);

	// const isInTerminalState = useMemo(
	// 	() =>
	// 		(!completeRequest.isLoading && !!completeRequest.data) ||
	// 		(!getStatusRequest.isLoading &&
	// 			!!getStatusRequest.data &&
	// 			(getStatusRequest.data?.Data?.Status ===
	// 				m.Bokio.Bank.Web.Controllers.AuthStatusResultStatusEnum.Failed_BankIdTokenAlreadyUsed ||
	// 				getStatusRequest.data?.Data?.Status ===
	// 					m.Bokio.Bank.Web.Controllers.AuthStatusResultStatusEnum.Failed_ExpiredTransaction ||
	// 				getStatusRequest.data?.Data?.Status ===
	// 					m.Bokio.Bank.Web.Controllers.AuthStatusResultStatusEnum.Failed_StartFailed ||
	// 				getStatusRequest.data?.Data?.Status ===
	// 					m.Bokio.Bank.Web.Controllers.AuthStatusResultStatusEnum.Failed_UserCancelled ||
	// 				getStatusRequest.data?.Data?.Status ===
	// 					m.Bokio.Bank.Web.Controllers.AuthStatusResultStatusEnum.Failed_UnknownReason ||
	// 				getStatusRequest.data?.Data?.Status === m.Bokio.Bank.Web.Controllers.AuthStatusResultStatusEnum.Error)),
	// 	[completeRequest.isLoading, completeRequest.data, getStatusRequest.isLoading, getStatusRequest.data],
	// );
	const state_cancelling = useMemo(() => isCancelling, [isCancelling]);

	const state_cancelled = useMemo(
		() =>
			stateHelper_startIsCompleted &&
			!stateHelper_completeIsCompleted &&
			!getStatusRequest.isLoading &&
			!!getStatusRequest.data &&
			getStatusRequest.data?.Data?.Status ===
				m.Bokio.Bank.Web.Controllers.AuthStatusResultStatusEnum.Failed_UserCancelled,
		[stateHelper_startIsCompleted, stateHelper_completeIsCompleted, getStatusRequest.data, getStatusRequest.isLoading],
	);

	const internalState: string = useMemo(() => {
		const activeStates: Record<string, { value: boolean; description: string }> = {
			state_initializing: {
				value: state_initializing,
				description: "Initial state. Component has just been loaded and no start() has been invoked.",
			},
			state_startFailed: {
				value: state_startFailed,
				description: "start() completed with an error. Terminal state.",
			},
			state_startRequested: {
				value: state_startRequested,
				description: "start() has been invoked and the request is in flight.",
			},
			state_started: {
				value: state_started,
				description: "start() has completed successfully. We have a bankId token and the initial QR code.",
			},
			state_runloop_fetchingStatus: {
				value: state_runloop_fetchingStatus,
				description:
					"We are currently polling for status after having initialized. This is a transient state that should only last a few milliseconds.",
			},
			state_running_hasInteractionStatus: {
				value: state_running_hasInteractionStatus,
				description: "We have received a new status while polling. The authentication status is not terminal.",
			},
			state_hasNonInteractionStatus: {
				value: state_hasNonInteractionStatus,
				description: "We have received a new status while polling. The authentication status is terminal.",
			},
			state_successful: {
				value: state_successful,
				description:
					"Auth status indicates a successful authentication. Need to invoke complete() to complete the process and obtain auth payload.",
			},
			state_completed: {
				value: state_completed,
				description: "The authentication process has completed. Auth payload is available. Terminal state.",
			},
			state_completedWithError: {
				value: state_completedWithError,
				description: "The authentication process has completed with an error.",
			},
			state_runlookp_fetchingStatusFailed: {
				value: state_runlookp_fetchingStatusFailed,
				description: "Failed to fetch the authentication status.",
			},
			state_cancelling: { value: state_cancelling, description: "The authentication process is being cancelled." },
			state_cancelled: { value: state_cancelled, description: "The authentication process has been cancelled." },
		};

		const trueStates = Object.entries(activeStates).filter(([, s]) => s.value);
		if (trueStates.length === 0) {
			return "Undefined";
		}
		if (trueStates.length > 1) {
			return `Multiple - ${trueStates.map(([k]) => k).join(", ")}`;
		}
		return trueStates[0][0];
	}, [
		state_initializing,
		state_startFailed,
		state_startRequested,
		state_runloop_fetchingStatus,
		state_running_hasInteractionStatus,
		state_hasNonInteractionStatus,
		state_started,
		state_successful,
		state_completed,
		state_completedWithError,
		state_runlookp_fetchingStatusFailed,
		state_cancelling,
		state_cancelled,
	]);

	useEffect(() => {
		resetGetStatus();
		resetComplete();
		resetCancel();
		start();
	}, [resetComplete, resetGetStatus, resetStart, resetCancel, start]);

	//This is the polling engine. Its responsibility is to call getStatus on certain states, to ensure running states are continously updated with the latest status from the backend. It also calls complete when we have a successful status, to complete the authentication and retrieve the tokens.
	useEffect(() => {
		if (
			(internalState === "state_started" || internalState === "state_running_hasInteractionStatus") &&
			!!stateHelper_bankIdToken
		) {
			getStatus(stateHelper_bankIdToken ?? "");
		}
	}, [getStatus, internalState, stateHelper_bankIdToken]);

	//This is the completion engine. Whenever the successful state is set, it will automatically invoke complete() to complete the process.
	useEffect(() => {
		if (internalState === "state_successful" && !!stateHelper_bankIdToken) {
			complete({ BankIdToken: stateHelper_bankIdToken ?? "" });
		}
	}, [stateHelper_bankIdToken, complete, internalState]);

	const authStatus = useMemo(() => {
		if (getStatusRequest.data) {
			return getStatusRequest.data;
		}
		if (startRequest.data) {
			return startRequest.data;
		}
		return null;
	}, [getStatusRequest.data, startRequest.data]);

	const authState: AuthenticationState = useMemo(() => {
		const status = authStatus?.Data?.Status;

		if (internalState === "state_cancelling") {
			return { type: "Cancelling", isTerminal: false };
		}
		if (internalState === "state_cancelled") {
			return { type: "Cancelled", isTerminal: true };
		}
		if (internalState === "state_startRequested" || internalState === "state_initializing") {
			return { type: "Initializing", isTerminal: false };
		}
		if (internalState === "state_startFailed" || internalState === "state_runlookp_fetchingStatusFailed") {
			return { type: "Error", isTerminal: true };
		}
		if (internalState === "state_successful") {
			return { type: "Completing", isTerminal: false };
		}
		if (internalState === "state_completedWithError") {
			if (completeRequest?.data?.Error === m.Bokio.Bank.Web.Controllers.AuthenticationError.PersonalNumberMismatch) {
				return {
					type: "Failed",
					isTerminal: true,
					reason: "PersonalNumberMismatch",
					message: "The personal number used for BankID authentication does not match your account.",
				};
			}
			return {
				type: "Error",
				isTerminal: true,
				message: completeRequest?.data?.ErrorMessage ?? "Authentication failed",
			};
		}
		if (internalState === "state_completed") {
			const psus =
				completeRequest?.data?.Data?.DelegateInformation?.PSUs?.map(psu => ({
					psuId: psu.PsuId,
					sveaDelegateId: psu.SveaDelegateId,
					accessLevel: (() => {
						switch (psu.AccessLevel) {
							case m.Bokio.Bank.Web.Controllers.CompleteResponseBodyDelegateInformationPsuInformationAccessLevelEnum
								.Restricted:
								return {
									id: "restricted" as const,
									contexts:
										psu.RestrictedAccessLevelContexts?.map(ctx => ({
											bankAccountId: ctx.BankAccountId,
											accessLevel: (() => {
												switch (ctx.AccessLevel) {
													case m.Bokio.Bank.Web.Controllers
														.CompleteResponseBodyDelegateInformationPsuInformationRestrictedAccessLevelContextAccessLevelEnum
														.Read:
														return "read" as const;
													case m.Bokio.Bank.Web.Controllers
														.CompleteResponseBodyDelegateInformationPsuInformationRestrictedAccessLevelContextAccessLevelEnum
														.PreparePayment:
														return "preparePayment" as const;
													case m.Bokio.Bank.Web.Controllers
														.CompleteResponseBodyDelegateInformationPsuInformationRestrictedAccessLevelContextAccessLevelEnum
														.ApprovePayment:
														return "approvePayment" as const;
													default:
														throw new Error(`Unknown restricted access level: ${ctx.AccessLevel}`);
												}
											})(),
										})) ?? [],
								};
							case m.Bokio.Bank.Web.Controllers.CompleteResponseBodyDelegateInformationPsuInformationAccessLevelEnum
								.FullAccess:
								return { id: "fullAccess" as const };
							case m.Bokio.Bank.Web.Controllers.CompleteResponseBodyDelegateInformationPsuInformationAccessLevelEnum
								.Approve:
								return { id: "approve" as const };
							case m.Bokio.Bank.Web.Controllers.CompleteResponseBodyDelegateInformationPsuInformationAccessLevelEnum
								.Prepare:
								return { id: "prepare" as const };
							case m.Bokio.Bank.Web.Controllers.CompleteResponseBodyDelegateInformationPsuInformationAccessLevelEnum
								.CardHolder:
								return { id: "cardHolder" as const };
							default:
								throw new Error(`Unknown access level: ${psu.AccessLevel}`);
						}
					})(),
				})) ?? [];
			return {
				type: "Completed",
				isTerminal: true,
				bearerToken: completeRequest?.data?.Data?.BearerToken ?? "",
				refreshToken: completeRequest?.data?.Data?.RefreshToken ?? "",
				delegateInformation: {
					correspondenceName: completeRequest?.data?.Data?.DelegateInformation?.CorrespondenceName ?? "",
					firstName: completeRequest?.data?.Data?.DelegateInformation?.FirstName ?? "",
					lastName: completeRequest?.data?.Data?.DelegateInformation?.LastName ?? "",
					personalNumber: completeRequest?.data?.Data?.DelegateInformation?.PersonalNumber ?? "",
					psus: psus,
					forPsu: (psuId: string) => {
						const psu = psus.find(p => p.psuId === psuId);
						return psu ?? null;
					},
				},
			};
		}
		if (status === m.Bokio.Bank.Web.Controllers.AuthStatusResultStatusEnum.PendingStart) {
			return { type: "PendingStart", isTerminal: false, qrCode: authStatus?.Data?.QrCode ?? undefined };
		}
		if (status === m.Bokio.Bank.Web.Controllers.AuthStatusResultStatusEnum.AwaitingAuthorization) {
			return { type: "AwaitingAuthorization", isTerminal: false };
		}
		if (status === m.Bokio.Bank.Web.Controllers.AuthStatusResultStatusEnum.Successful) {
			return { type: "Completing", isTerminal: false };
		}
		if (status === m.Bokio.Bank.Web.Controllers.AuthStatusResultStatusEnum.Failed_BankIdTokenAlreadyUsed) {
			return { type: "Failed", isTerminal: true, reason: "BankIdTokenAlreadyUsed" };
		}
		if (status === m.Bokio.Bank.Web.Controllers.AuthStatusResultStatusEnum.Failed_ExpiredTransaction) {
			return { type: "Failed", isTerminal: true, reason: "ExpiredTransaction" };
		}
		if (status === m.Bokio.Bank.Web.Controllers.AuthStatusResultStatusEnum.Failed_StartFailed) {
			return { type: "Failed", isTerminal: true, reason: "StartFailed" };
		}
		if (status === m.Bokio.Bank.Web.Controllers.AuthStatusResultStatusEnum.Failed_UserCancelled) {
			return { type: "Failed", isTerminal: true, reason: "UserCancelled" };
		}
		if (status === m.Bokio.Bank.Web.Controllers.AuthStatusResultStatusEnum.Failed_UnknownReason) {
			return {
				type: "Failed",
				isTerminal: true,
				reason: "UnknownReason",
				message: authStatus?.Data?.Message ?? undefined,
			};
		}
		if (status === m.Bokio.Bank.Web.Controllers.AuthStatusResultStatusEnum.Error) {
			return { type: "Error", isTerminal: true, message: authStatus?.Data?.Message ?? undefined };
		}

		// if (internalState === "Polling - fetching status" || internalState === "Polling - has interaction status") {
		// 	return { type: "Polling", isTerminal: false };
		// }

		return { type: "Initializing", isTerminal: false };
	}, [
		authStatus?.Data?.Message,
		authStatus?.Data?.QrCode,
		authStatus?.Data?.Status,
		completeRequest?.data,
		internalState,
	]);

	const handleCancel = useCallback(() => {
		if (isCancelling) {
			return;
		}
		if (stateHelper_bankIdToken) {
			cancel({ BankIdToken: stateHelper_bankIdToken });
		}
		setIsCancelling(true);
	}, [stateHelper_bankIdToken, isCancelling, cancel]);

	const handleRetry = useCallback(() => {
		if (stateHelper_bankIdToken) {
			cancel({ BankIdToken: stateHelper_bankIdToken });
		}
		setIsCancelling(false);
		resetStart();
		resetGetStatus();
		resetComplete();
		resetCancel();
		start();
	}, [stateHelper_bankIdToken, cancel, resetStart, resetGetStatus, resetComplete, resetCancel, start]);

	const debugInfo: Record<string, unknown> = {
		"Internal State": internalState,
		"Authentication State": authState.type,
		"BankId Token": stateHelper_bankIdToken,
		"Start Request": startRequest,
		"Status Request": getStatusRequest,
		"Complete Request": completeRequest,
		"Cancel Request": cancelRequest,
		"Auth Status": authStatus,
	};

	return {
		state: authState,
		retry: handleRetry,
		cancel: handleCancel,
		debugInfo,
		bankIdToken: stateHelper_bankIdToken,
	};
};
